import React, { useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronLeft, ChevronRight, User, Shield, BookOpen, Heart, FileText, Upload, AlertCircle, Info } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
const ProfileSetup = () => {
  const [step, setStep] = useState(1);
  const [profileData, setProfileData] = useState({
    name: '',
    age: '',
    gender: '',
    education: '',
    profession: '',
    religiousOutlook: '',
    tribe: '',
    aboutMe: '',
    familyInvolvement: 'required',
    photoPrivacy: 'avatar',
    marriageStatus: '',
    guardianType: '',
    guardianName: '',
    guardianRelationship: '',
    guardianPhone: '',
    guardianEmail: ''
  });
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const nextStep = () => {
    setStep(prev => prev + 1);
  };
  const prevStep = () => {
    setStep(prev => prev - 1);
  };
  const renderStep = () => {
    switch (step) {
      case 1:
        return <div className="space-y-6" data-id="element-787">
            <div className="bg-emerald-50 p-4 rounded-lg mb-6" data-id="element-788">
              <div className="flex items-start" data-id="element-789">
                <Info className="h-5 w-5 text-emerald-700 mr-2 mt-0.5 flex-shrink-0" data-id="element-790" />
                <div data-id="element-791">
                  <h3 className="font-medium text-emerald-900 mb-1" data-id="element-792">
                    Registration Overview
                  </h3>
                  <p className="text-sm text-emerald-800" data-id="element-793">
                    You'll complete 6 steps: UAE Pass verification, marriage
                    status verification, guardian assignment, basic information,
                    personal values, and privacy preferences.
                  </p>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-emerald-800 mb-6" data-id="element-794">
              Step 1: UAE Pass Verification
            </h2>
            <p className="mb-8 text-gray-700" data-id="element-795">
              To ensure the security and authenticity of our community, we
              require verification through UAE Pass.
            </p>
            <div className="bg-amber-50 p-6 rounded-lg mb-8" data-id="element-796">
              <div className="flex items-center mb-4" data-id="element-797">
                <Shield className="h-6 w-6 text-emerald-700 mr-3" data-id="element-798" />
                <h3 className="text-lg font-semibold text-emerald-900" data-id="element-799">
                  Why We Verify
                </h3>
              </div>
              <ul className="space-y-3 text-gray-700" data-id="element-800">
                <li className="flex items-start" data-id="element-801">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-802" />
                  <span data-id="element-803">Ensures all users are verified Emiratis</span>
                </li>
                <li className="flex items-start" data-id="element-804">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-805" />
                  <span data-id="element-806">Protects the community from fake profiles</span>
                </li>
                <li className="flex items-start" data-id="element-807">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-808" />
                  <span data-id="element-809">
                    Keeps your personal data secure with government-grade
                    encryption
                  </span>
                </li>
              </ul>
            </div>
            <button className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-3 px-4 rounded-lg transition duration-300 flex items-center justify-center" onClick={nextStep} data-id="element-810">
              Verify with UAE Pass
              <ChevronRight className="ml-2 h-5 w-5" data-id="element-811" />
            </button>
            <p className="text-sm text-gray-500 text-center mt-4" data-id="element-812">
              We only verify your Emirati status. Your personal data remains
              protected.
            </p>
          </div>;
      case 2:
        return <div className="space-y-6" data-id="element-813">
            <div className="bg-amber-50 p-4 rounded-lg mb-6" data-id="element-814">
              <div className="flex items-start" data-id="element-815">
                <AlertCircle className="h-5 w-5 text-amber-700 mr-2 mt-0.5 flex-shrink-0" data-id="element-816" />
                <div data-id="element-817">
                  <h3 className="font-medium text-amber-900 mb-1" data-id="element-818">
                    Important Requirement
                  </h3>
                  <p className="text-sm text-amber-800" data-id="element-819">
                    All users must verify they are not currently married by
                    submitting official documentation.
                  </p>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-emerald-800 mb-6" data-id="element-820">
              Step 2: Marriage Status Verification
            </h2>
            <div className="space-y-4" data-id="element-821">
              <div data-id="element-822">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-823">
                  Current Marriage Status
                </label>
                <select name="marriageStatus" value={profileData.marriageStatus} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" data-id="element-824">
                  <option value="" data-id="element-825">Select your status</option>
                  <option value="never-married" data-id="element-826">Never Married</option>
                  <option value="divorced" data-id="element-827">Divorced</option>
                  <option value="widowed" data-id="element-828">Widowed</option>
                </select>
              </div>
              <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-emerald-500 transition-colors duration-300 cursor-pointer" data-id="element-829">
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-830" />
                <h3 className="font-medium text-gray-900 mb-2" data-id="element-831">
                  Upload Marriage Status Certificate
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-832">
                  Official document from UAE government verifying you are not
                  currently married
                </p>
                <button className="bg-emerald-700 hover:bg-emerald-800 text-white font-medium py-2 px-6 rounded-lg transition duration-300" data-id="element-833">
                  Choose File
                </button>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg" data-id="element-834">
                <div className="flex items-start" data-id="element-835">
                  <FileText className="h-5 w-5 text-blue-700 mr-2 mt-0.5 flex-shrink-0" data-id="element-836" />
                  <div data-id="element-837">
                    <h4 className="font-medium text-blue-900 mb-1" data-id="element-838">
                      Accepted Documents
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-1" data-id="element-839">
                      <li data-id="element-840">• Single Status Certificate from UAE courts</li>
                      <li data-id="element-841">• Divorce Certificate (if previously married)</li>
                      <li data-id="element-842">• Death Certificate of spouse (if widowed)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-between pt-4" data-id="element-843">
              <button className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition duration-300 flex items-center" onClick={prevStep} data-id="element-844">
                <ChevronLeft className="mr-2 h-5 w-5" data-id="element-845" />
                Back
              </button>
              <button className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-2 px-4 rounded-lg transition duration-300 flex items-center" onClick={nextStep} data-id="element-846">
                Continue
                <ChevronRight className="ml-2 h-5 w-5" data-id="element-847" />
              </button>
            </div>
          </div>;
      case 3:
        return <div className="space-y-6" data-id="element-848">
            <div className="bg-amber-50 p-4 rounded-lg mb-6" data-id="element-849">
              <div className="flex items-start" data-id="element-850">
                <Shield className="h-5 w-5 text-amber-700 mr-2 mt-0.5 flex-shrink-0" data-id="element-851" />
                <div data-id="element-852">
                  <h3 className="font-medium text-amber-900 mb-1" data-id="element-853">
                    Guardian Requirement
                  </h3>
                  <p className="text-sm text-amber-800" data-id="element-854">
                    All users must have a guardian. If your parents are alive,
                    one of them must be your guardian. Otherwise, you'll need to
                    provide documentation for an alternative guardian.
                  </p>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-emerald-800 mb-6" data-id="element-855">
              Step 3: Guardian Assignment
            </h2>
            <div className="space-y-4" data-id="element-856">
              <div data-id="element-857">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-858">
                  Guardian Type
                </label>
                <select name="guardianType" value={profileData.guardianType} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" data-id="element-859">
                  <option value="" data-id="element-860">Select guardian type</option>
                  <option value="parent" data-id="element-861">Parent (Father or Mother)</option>
                  <option value="alternative" data-id="element-862">Alternative Guardian</option>
                </select>
              </div>
              {profileData.guardianType === 'alternative' && <div className="bg-blue-50 p-4 rounded-lg" data-id="element-863">
                  <div className="flex items-start mb-4" data-id="element-864">
                    <AlertCircle className="h-5 w-5 text-blue-700 mr-2 mt-0.5 flex-shrink-0" data-id="element-865" />
                    <div data-id="element-866">
                      <h4 className="font-medium text-blue-900 mb-1" data-id="element-867">
                        Additional Documentation Required
                      </h4>
                      <p className="text-sm text-blue-800" data-id="element-868">
                        You must submit official documentation proving both
                        parents are deceased or unable to serve as guardian.
                      </p>
                    </div>
                  </div>
                  <div className="bg-white border-2 border-dashed border-blue-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors duration-300 cursor-pointer" data-id="element-869">
                    <Upload className="h-10 w-10 text-blue-400 mx-auto mb-3" data-id="element-870" />
                    <p className="text-sm text-gray-700 mb-3" data-id="element-871">
                      Upload death certificates or legal documentation
                    </p>
                    <button className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-300" data-id="element-872">
                      Upload Documents
                    </button>
                  </div>
                </div>}
              <div data-id="element-873">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-874">
                  Guardian's Full Name
                </label>
                <input type="text" name="guardianName" value={profileData.guardianName} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Enter guardian's full name" data-id="element-875" />
              </div>
              <div data-id="element-876">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-877">
                  Relationship to Guardian
                </label>
                <select name="guardianRelationship" value={profileData.guardianRelationship} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" data-id="element-878">
                  <option value="" data-id="element-879">Select relationship</option>
                  <option value="father" data-id="element-880">Father</option>
                  <option value="mother" data-id="element-881">Mother</option>
                  <option value="brother" data-id="element-882">Brother</option>
                  <option value="uncle" data-id="element-883">Uncle</option>
                  <option value="grandfather" data-id="element-884">Grandfather</option>
                  <option value="other" data-id="element-885">Other Male Relative</option>
                </select>
              </div>
              <div data-id="element-886">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-887">
                  Guardian's Phone Number
                </label>
                <input type="tel" name="guardianPhone" value={profileData.guardianPhone} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="+971 XX XXX XXXX" data-id="element-888" />
              </div>
              <div data-id="element-889">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-890">
                  Guardian's Email Address
                </label>
                <input type="email" name="guardianEmail" value={profileData.guardianEmail} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="guardian@example.com" data-id="element-891" />
                <p className="text-sm text-gray-500 mt-1" data-id="element-892">
                  Your guardian will receive an email invitation to create their
                  guardian account
                </p>
              </div>
            </div>
            <div className="flex justify-between pt-4" data-id="element-893">
              <button className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition duration-300 flex items-center" onClick={prevStep} data-id="element-894">
                <ChevronLeft className="mr-2 h-5 w-5" data-id="element-895" />
                Back
              </button>
              <button className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-2 px-4 rounded-lg transition duration-300 flex items-center" onClick={nextStep} data-id="element-896">
                Continue
                <ChevronRight className="ml-2 h-5 w-5" data-id="element-897" />
              </button>
            </div>
          </div>;
      case 4:
        return <div className="space-y-6" data-id="element-898">
            <h2 className="text-2xl font-bold text-emerald-800 mb-6" data-id="element-899">
              Step 4: Basic Information
            </h2>
            <div className="space-y-4" data-id="element-900">
              <div data-id="element-901">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-902">
                  Full Name
                </label>
                <input type="text" name="name" value={profileData.name} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Enter your full name" data-id="element-903" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-904">
                <div data-id="element-905">
                  <label className="block text-gray-700 font-medium mb-2" data-id="element-906">
                    Age
                  </label>
                  <input type="number" name="age" value={profileData.age} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Your age" data-id="element-907" />
                </div>
                <div data-id="element-908">
                  <label className="block text-gray-700 font-medium mb-2" data-id="element-909">
                    Gender
                  </label>
                  <select name="gender" value={profileData.gender} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" data-id="element-910">
                    <option value="" data-id="element-911">Select gender</option>
                    <option value="male" data-id="element-912">Male</option>
                    <option value="female" data-id="element-913">Female</option>
                  </select>
                </div>
              </div>
              <div data-id="element-914">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-915">
                  Education Level
                </label>
                <select name="education" value={profileData.education} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" data-id="element-916">
                  <option value="" data-id="element-917">Select education level</option>
                  <option value="high-school" data-id="element-918">High School</option>
                  <option value="diploma" data-id="element-919">Diploma</option>
                  <option value="bachelors" data-id="element-920">Bachelor's Degree</option>
                  <option value="masters" data-id="element-921">Master's Degree</option>
                  <option value="doctorate" data-id="element-922">Doctorate</option>
                </select>
              </div>
              <div data-id="element-923">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-924">
                  Profession
                </label>
                <input type="text" name="profession" value={profileData.profession} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Your profession or field of work" data-id="element-925" />
              </div>
            </div>
            <div className="flex justify-between pt-4" data-id="element-926">
              <button className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition duration-300 flex items-center" onClick={prevStep} data-id="element-927">
                <ChevronLeft className="mr-2 h-5 w-5" data-id="element-928" />
                Back
              </button>
              <button className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-2 px-4 rounded-lg transition duration-300 flex items-center" onClick={nextStep} data-id="element-929">
                Continue
                <ChevronRight className="ml-2 h-5 w-5" data-id="element-930" />
              </button>
            </div>
          </div>;
      case 5:
        return <div className="space-y-6" data-id="element-931">
            <h2 className="text-2xl font-bold text-emerald-800 mb-6" data-id="element-932">
              Step 5: Personal Values & Background
            </h2>
            <div className="space-y-4" data-id="element-933">
              <div data-id="element-934">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-935">
                  Religious Outlook
                </label>
                <select name="religiousOutlook" value={profileData.religiousOutlook} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" data-id="element-936">
                  <option value="" data-id="element-937">Select your religious outlook</option>
                  <option value="sunni" data-id="element-938">Sunni</option>
                  <option value="shia" data-id="element-939">Shia</option>
                  <option value="ibadi" data-id="element-940">Ibadi</option>
                  <option value="other" data-id="element-941">Other</option>
                </select>
              </div>
              <div data-id="element-942">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-943">
                  Tribe/Family Name{' '}
                  <span className="text-sm text-gray-500" data-id="element-944">(Optional)</span>
                </label>
                <input type="text" name="tribe" value={profileData.tribe} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Your tribe or family name" data-id="element-945" />
                <p className="text-sm text-gray-500 mt-1" data-id="element-946">
                  This information is optional and can be hidden from your
                  public profile.
                </p>
              </div>
              <div data-id="element-947">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-948">
                  About Me
                </label>
                <textarea name="aboutMe" value={profileData.aboutMe} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 h-32" placeholder="Share a bit about yourself, your interests, and what you're looking for in a spouse..." data-id="element-949"></textarea>
              </div>
            </div>
            <div className="flex justify-between pt-4" data-id="element-950">
              <button className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition duration-300 flex items-center" onClick={prevStep} data-id="element-951">
                <ChevronLeft className="mr-2 h-5 w-5" data-id="element-952" />
                Back
              </button>
              <button className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-2 px-4 rounded-lg transition duration-300 flex items-center" onClick={nextStep} data-id="element-953">
                Continue
                <ChevronRight className="ml-2 h-5 w-5" data-id="element-954" />
              </button>
            </div>
          </div>;
      case 6:
        return <div className="space-y-6" data-id="element-955">
            <h2 className="text-2xl font-bold text-emerald-800 mb-6" data-id="element-956">
              Step 6: Privacy Preferences
            </h2>
            <div className="space-y-6" data-id="element-957">
              <div className="bg-amber-50 p-5 rounded-lg" data-id="element-958">
                <h3 className="text-lg font-semibold text-emerald-900 mb-4 flex items-center" data-id="element-959">
                  <User className="h-5 w-5 mr-2" data-id="element-960" />
                  Profile Photo Privacy
                </h3>
                <div className="space-y-3" data-id="element-961">
                  <label className="flex items-start p-3 border border-gray-200 rounded-lg bg-white cursor-pointer hover:border-emerald-300 transition" data-id="element-962">
                    <input type="radio" name="photoPrivacy" value="avatar" checked={profileData.photoPrivacy === 'avatar'} onChange={handleChange} className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300" data-id="element-963" />
                    <div className="ml-3" data-id="element-964">
                      <p className="font-medium text-gray-800" data-id="element-965">
                        Default Avatar
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-966">
                        Use a stylized avatar instead of your real photo.
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start p-3 border border-gray-200 rounded-lg bg-white cursor-pointer hover:border-emerald-300 transition" data-id="element-967">
                    <input type="radio" name="photoPrivacy" value="blurred" checked={profileData.photoPrivacy === 'blurred'} onChange={handleChange} className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300" data-id="element-968" />
                    <div className="ml-3" data-id="element-969">
                      <p className="font-medium text-gray-800" data-id="element-970">Blurred Photo</p>
                      <p className="text-sm text-gray-600" data-id="element-971">
                        Use your real photo but with a blur effect that can be
                        removed later.
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start p-3 border border-gray-200 rounded-lg bg-white cursor-pointer hover:border-emerald-300 transition" data-id="element-972">
                    <input type="radio" name="photoPrivacy" value="real" checked={profileData.photoPrivacy === 'real'} onChange={handleChange} className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300" data-id="element-973" />
                    <div className="ml-3" data-id="element-974">
                      <p className="font-medium text-gray-800" data-id="element-975">Real Photo</p>
                      <p className="text-sm text-gray-600" data-id="element-976">
                        Use your real photo (screenshot protected).
                      </p>
                    </div>
                  </label>
                </div>
              </div>
              <div className="bg-emerald-50 p-5 rounded-lg" data-id="element-977">
                <h3 className="text-lg font-semibold text-emerald-900 mb-3 flex items-center" data-id="element-978">
                  <Shield className="h-5 w-5 mr-2" data-id="element-979" />
                  Guardian Control Level
                </h3>
                <p className="text-sm text-emerald-800 mb-4" data-id="element-980">
                  Your guardian will have full oversight of your marriage
                  journey. They can monitor conversations, chat directly with
                  other guardians, and accept or decline matches.
                </p>
                <div className="bg-white p-4 rounded-lg border border-emerald-200" data-id="element-981">
                  <div className="space-y-2" data-id="element-982">
                    <div className="flex items-center" data-id="element-983">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mr-2" data-id="element-984" />
                      <span className="text-sm text-gray-700" data-id="element-985">
                        Monitor all your conversations
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-986">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mr-2" data-id="element-987" />
                      <span className="text-sm text-gray-700" data-id="element-988">
                        Chat directly with other guardians
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-989">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mr-2" data-id="element-990" />
                      <span className="text-sm text-gray-700" data-id="element-991">
                        Take over chat conversations when needed
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-992">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mr-2" data-id="element-993" />
                      <span className="text-sm text-gray-700" data-id="element-994">
                        Accept or decline matches on your behalf
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-between pt-4" data-id="element-995">
              <button className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition duration-300 flex items-center" onClick={prevStep} data-id="element-996">
                <ChevronLeft className="mr-2 h-5 w-5" data-id="element-997" />
                Back
              </button>
              <Link to="/login" className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-2 px-4 rounded-lg transition duration-300 flex items-center" data-id="element-998">
                Complete Profile
                <CheckCircle2 className="ml-2 h-5 w-5" data-id="element-999" />
              </Link>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-1000">
      <Header data-id="element-1001" />
      <main className="flex-grow py-12 px-4" data-id="element-1002">
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-md overflow-hidden" data-id="element-1003">
          <div className="p-8" data-id="element-1004">
            <div className="mb-8" data-id="element-1005">
              <div className="flex items-center justify-between mb-4" data-id="element-1006">
                <h1 className="text-3xl font-bold text-emerald-900" data-id="element-1007">
                  Create Your Profile
                </h1>
                <span className="text-sm text-gray-500" data-id="element-1008">Step {step} of 6</span>
              </div>
              <div className="relative pt-1" data-id="element-1009">
                <div className="flex mb-2 items-center justify-between" data-id="element-1010">
                  {[1, 2, 3, 4, 5, 6].map(s => <Fragment key={s} data-id="element-1011">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full ${step >= s ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-1012">
                        {s === 1 && <Shield className="h-4 w-4" data-id="element-1013" />}
                        {s === 2 && <FileText className="h-4 w-4" data-id="element-1014" />}
                        {s === 3 && <Shield className="h-4 w-4" data-id="element-1015" />}
                        {s === 4 && <User className="h-4 w-4" data-id="element-1016" />}
                        {s === 5 && <BookOpen className="h-4 w-4" data-id="element-1017" />}
                        {s === 6 && <Heart className="h-4 w-4" data-id="element-1018" />}
                      </div>
                      {s < 6 && <div className={`h-1 flex-1 ${step > s ? 'bg-emerald-600' : 'bg-gray-200'}`} data-id="element-1019"></div>}
                    </Fragment>)}
                </div>
              </div>
            </div>
            {renderStep()}
          </div>
        </div>
      </main>
      <Footer data-id="element-1020" />
    </div>;
};
export default ProfileSetup;